// FAQ data utility — flatten nested JSON into searchable articles with slugs
import faqData from "@/data/replenico-faqs.json";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface FaqArticle {
  question: string;
  answer: string;
  images?: string[];
  slug: string;
  categoryId: string;
  categoryName: string;
  subcategoryId?: string;
  subcategoryName?: string;
}

export interface FaqCategory {
  id: string;
  name: string;
  articleCount: number;
  subcategories?: { id: string; name: string; articleCount: number }[];
}

// ─── Slug generation ──────────────────────────────────────────────────────────

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/['']/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

// ─── Flatten all articles from nested JSON ────────────────────────────────────

interface RawArticle {
  question: string;
  answer: string;
  images?: string[];
}

interface RawSubcategory {
  id: string;
  name: string;
  articles?: RawArticle[];
  subcategories?: RawSubcategory[];
}

interface RawCategory {
  id: string;
  name: string;
  articles?: RawArticle[];
  subcategories?: RawSubcategory[];
}

function extractArticles(
  articles: RawArticle[] | undefined,
  categoryId: string,
  categoryName: string,
  subcategoryId?: string,
  subcategoryName?: string
): FaqArticle[] {
  if (!articles) return [];
  return articles.map((a) => ({
    question: a.question,
    answer: a.answer,
    images: a.images,
    slug: slugify(a.question),
    categoryId,
    categoryName,
    subcategoryId,
    subcategoryName,
  }));
}

function flattenSubcategories(
  subs: RawSubcategory[] | undefined,
  categoryId: string,
  categoryName: string
): FaqArticle[] {
  if (!subs) return [];
  const result: FaqArticle[] = [];
  for (const sub of subs) {
    result.push(
      ...extractArticles(sub.articles, categoryId, categoryName, sub.id, sub.name)
    );
    if (sub.subcategories) {
      // Nested subcategories (e.g. plumbing-maintenance > specific-issues)
      for (const nested of sub.subcategories) {
        result.push(
          ...extractArticles(
            nested.articles,
            categoryId,
            categoryName,
            nested.id,
            `${sub.name} › ${nested.name}`
          )
        );
        if (nested.subcategories) {
          result.push(
            ...flattenSubcategories(nested.subcategories, categoryId, categoryName)
          );
        }
      }
    }
  }
  return result;
}

// Build the complete flat article list once
function buildAllArticles(): FaqArticle[] {
  const categories = faqData.categories as RawCategory[];
  const result: FaqArticle[] = [];

  for (const cat of categories) {
    result.push(...extractArticles(cat.articles, cat.id, cat.name));
    result.push(...flattenSubcategories(cat.subcategories, cat.id, cat.name));
  }

  return result;
}

// ─── Cached exports ───────────────────────────────────────────────────────────

/** All FAQ articles, flattened with slugs and category info */
export const allArticles: FaqArticle[] = buildAllArticles();

/** All top-level categories with article counts */
export const allCategories: FaqCategory[] = (
  faqData.categories as RawCategory[]
).map((cat) => {
  const articles = allArticles.filter((a) => a.categoryId === cat.id);
  const subcategoryMap = new Map<string, { id: string; name: string; count: number }>();

  for (const a of articles) {
    if (a.subcategoryId && a.subcategoryName) {
      const existing = subcategoryMap.get(a.subcategoryId);
      if (existing) {
        existing.count++;
      } else {
        subcategoryMap.set(a.subcategoryId, {
          id: a.subcategoryId,
          name: a.subcategoryName,
          count: 1,
        });
      }
    }
  }

  return {
    id: cat.id,
    name: cat.name,
    articleCount: articles.length,
    subcategories: subcategoryMap.size > 0
      ? Array.from(subcategoryMap.values()).map((s) => ({
          id: s.id,
          name: s.name,
          articleCount: s.count,
        }))
      : undefined,
  };
});

/** Get articles for a specific category */
export function getArticlesByCategory(categoryId: string): FaqArticle[] {
  return allArticles.filter((a) => a.categoryId === categoryId);
}

/** Get a single article by category + slug */
export function getArticle(
  categoryId: string,
  slug: string
): FaqArticle | undefined {
  return allArticles.find(
    (a) => a.categoryId === categoryId && a.slug === slug
  );
}

/** Get category info by id */
export function getCategory(categoryId: string): FaqCategory | undefined {
  return allCategories.find((c) => c.id === categoryId);
}

/** Search articles by query string */
export function searchArticles(query: string): FaqArticle[] {
  if (!query.trim()) return allArticles;
  const q = query.toLowerCase();
  return allArticles.filter(
    (a) =>
      a.question.toLowerCase().includes(q) ||
      a.answer.toLowerCase().includes(q) ||
      a.categoryName.toLowerCase().includes(q) ||
      (a.subcategoryName && a.subcategoryName.toLowerCase().includes(q))
  );
}

/** FAQ last updated date */
export const faqLastUpdated: string = faqData.lastUpdated;
